import axios from "axios"
import { Application, Container, EventSystem, Graphics, InteractionManager, Point, Sprite, Text } from 'pixi.js';
import { Pic } from "./Pic";
import { PicSprite } from "./PicSprite";
import { PicLabel } from "./PicLabel";
import { PicBackground } from "./PicBackground";
import { PicComplete } from "./PicComplete";
import { Viewport } from "./Viewport";
import { PicNode } from "./PicNode";
import { SpatialOptimizer } from "./SpatialOptimizer";

// Ancho (en unidades del dibujo) de las líneas de elementos mecánicos
const BORDER_WORLD_WIDTH = 0.15;
// Si esa línea mide menos de estos píxeles en pantalla, se pasa a línea fija de 1px
const PIXEL_LINE_SCREEN_THRESHOLD = 1;
// Espera tras el último zoom antes de comprobar si hay que cambiar de modo
const LINES_MODE_DEBOUNCE_MS = 150;

/** Level in "message" events. */
const MessageLevel = Object.freeze({
    INFO: "info",
    WARN: "warn",
    ERROR: "error"
})

export class PixiViewer {

    constructor(domContainer, stage, options = null) {
      this.options = Object.create(PixiViewer.DefaultOptions)
      if (options) {
          Object.assign(this.options, options)
      }
      options = this.options

      this.domContainer = domContainer
      this.stage = stage

      // Create a PixiJS application.
      this.app = null
      this.picsContainer = null
      this.backsContainer = null
      this.bordersContainer = null
      this.labelsContainer = null
      this.globalContainer = null

      this.entities = []
      this.labels = []
      this.bounds = null
      this.origin = null
      this.layers = []

      this.zoomLevel = 1
      this.dragging = false
      this.selecting = false
      this.isSelectingDragging = false // Flag para saber si realmente se está arrastrando en modo selección
      this.workLayer = null

      this.boxSelect = null
      this.selectionBoxStartX = 0;
      this.selectionBoxStartY = 0;
      this.selectionBoxEndX = 0;
      this.selectionBoxEndY = 0;
      this.selectionBoxWidth = 0;
      this.selectionBoxHeight = 0;

      this.dragData = null

      this.renderer = null

      // Optimizaciones: Spatial Grid y Viewport Culling
      this.spatialOptimizer = new SpatialOptimizer(200) // Tamaño de celda del grid (ajustable)

      // Estadísticas de culling para verificación
      this.cullingStats = { visible: 0, total: 0, culled: 0 }

      // Event delegation - variables para tracking
      this._hoveredPic = null;
      this._lastClickTime = 0;
      this._clickTimeout = null;
      this._useSprites = true; // Flag para usar PicSprite en lugar de Pic (desactivado - requiere más trabajo)

      // Labels - creados al inicio con resolución reducida (sin LOD dinámico)
      this._labelsCreated = false;

      // Backgrounds combinados en una sola textura
      this._backgroundsGraphics = null;

      // Throttle para emisión de eventos de viewport (sincronización con Google Maps)
      this._viewportChangeScheduled = false;

      // Bordes híbridos: con zoom lejano se dibujan a 1px fijo en pantalla (pixelLine),
      // con zoom cercano con su ancho real. Solo se redibujan al cruzar el umbral.
      this._bordersPixelLine = false;
      this._linesModeTimeout = null;

      // this.layerEntities = this.stage.getLayers().find( (ly) => { return ly.attrs.id == 'layerEntities' } ).getLayer()
      // this.canvaEntities = this.layerEntities ? this.layerEntities.getNativeCanvasElement() : null

      // if (options.autoResize) {
        // this.canvasWidth = domContainer.clientWidth
        this.canvasWidth = domContainer.offsetWidth
        // this.canvasHeight = domContainer.clientHeight
        this.canvasHeight = domContainer.offsetHeight

        domContainer.style.position = "relative"
      // } else {
      //     this.canvasWidth = options.canvasWidth
      //     this.canvasHeight = options.canvasHeight
      //     this.resizeObserver = null
      // }

      this._SetSize(this.canvasWidth, this.canvasHeight)

      // domContainer.style.display = "block"
      // if (options.autoResize) {
      //     this.canvas.style.position = "absolute"
      //     this.resizeObserver = new ResizeObserver(entries => this._OnResize(entries[0]))
      //     this.resizeObserver.observe(domContainer)
      // }



      // Evento de scroll del mouse para controlar el zoom
      // window.addEventListener('wheel', (e) => { this._HandleWheel(e) })
      this._resizeHandler = () => { this._OnResize() }
      window.addEventListener('resize', this._resizeHandler)

      // Handler para limpiar recursos cuando se refresca la página
      this._beforeUnloadHandler = () => { this.Destroy() }
      window.addEventListener('beforeunload', this._beforeUnloadHandler)

      this._onMouseDown2 = this._onMouseDown2.bind(this);
      this._onMouseMove2 = this._onMouseMove2.bind(this);
      this._onMouseUp2 = this._onMouseUp2.bind(this);

      this._Init()
    }

    async _Init() {
      this.app = new Application();

      // Intialize the application.
      // IMPORTANTE: Para soportar transparencia dinámica, usamos backgroundAlpha: 0
      // y controlamos el fondo via CSS cuando no queremos transparencia
      await this.app.init({
        background: 0x000000, // Color base (se verá solo si alpha > 0)
        backgroundAlpha: 0, // Iniciar con alpha 0 para soportar transparencia
        resizeTo: this.domContainer,
        hello: true,
        preference: 'webgl',
        antialias: true,
        sharedTicker: true,
        resolution: 1,
        width: this.canvasWidth,
        height: this.canvasHeight,
        useBackBuffer: true,
        clearBeforeRender: true,
      });

      this.app.renderer.resize(this.canvasWidth, this.canvasHeight);
      this.stage.appendChild(this.app.canvas);

      // Establecer fondo visual por defecto via CSS (el canvas es transparente internamente)
      this.app.canvas.style.backgroundColor = '#ffffff';
      this._transparentBackground = false;

      this.viewport = new Viewport(this.app.renderer, this.canvasWidth, this.canvasHeight, this.app.stage)
      this.viewport.resize()

      this.globalContainer = new Container({
      })

      this.backsContainer = new Container({
        x: 10,
        y: 10,
        isRenderGroup: true,
        interactiveChildren: false,
      });

      this.picsContainer = new Container({
        x: 10,
        y: 10,
        interactive: true,
      });

      this.bordersContainer = new Container({
        x: 10,
        y: 10,
        interactiveChildren: false,
        eventMode: 'none',
      });

      this.labelsContainer = new Container({
        x: 10,
        y: 10,
        interactiveChildren: false,
        eventMode: 'none',
      });

      // Orden: fondos → sprites relleno → bordes → labels (encima de todo)
      this.globalContainer.addChild(this.backsContainer, this.picsContainer, this.bordersContainer, this.labelsContainer);
      this.app.stage.addChild(this.globalContainer);


      // // Agregar evento de clic al contenedor
      this.app.stage.eventMode = 'static';
      this.app.stage.hitArea = this.app.screen;
      this.app.stage.on('pointerdown', this._onMouseDown2);

      // this.globalContainer.eventMode = 'static';
      // this.globalContainer.hitArea = this.app.screen;
      // this.globalContainer.on('pointerdown', this._onMouseDownGlobalContainer);

      // caja de seleccion
      this.boxSelect = document.createElement('div')
      this.boxSelect.setAttribute('id', 'tpSelectBox')
      this.boxSelect.style.position = 'absolute'
      this.boxSelect.style.display = 'none'
      this.boxSelect.style.pointerEvents = 'none'
      this.boxSelect.style.left = '0px'
      this.boxSelect.style.top = '0px'
      this.boxSelect.style.width = '0px'
      this.boxSelect.style.height = '0px'
      // this.boxSelect.style.border = '1px solid black'

      this.stage.appendChild(this.boxSelect)

      // Activar viewport culling para optimización de rendimiento
      this.app.ticker.add(() => {
        this._performCulling();
      });

      // Configurar event delegation (reduce ~30,000 listeners a 3)
      this._setupEventDelegation();
    }

    /**
     * Configura event delegation en el contenedor de pics
     * Un solo listener maneja todos los eventos en lugar de miles de listeners individuales
     */
    _setupEventDelegation() {
      // Bindear métodos para mantener contexto
      this._onPicsPointerMove = this._onPicsPointerMove.bind(this);
      this._onPicsPointerDown = this._onPicsPointerDown.bind(this);
      this._onWheel = this._onWheel.bind(this);

      // Hover: listener nativo en el canvas DOM (no compite con listeners de PixiJS)
      this._canvasMouseMove = (e) => {
        const rect = this.app.canvas.getBoundingClientRect();
        const global = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        this._onPicsPointerMove(global);
      };
      this.app.canvas.addEventListener('mousemove', this._canvasMouseMove);

      // Zoom con la rueda del mouse (zoom-to-pointer). passive:false para poder
      // preventDefault y evitar el scroll de la página al hacer zoom.
      this.app.canvas.addEventListener('wheel', this._onWheel, { passive: false });

      // Click: sigue en picsContainer via PixiJS
      this.picsContainer.on('pointerdown', this._onPicsPointerDown);
    }

    /**
     * Encuentra el Pic bajo un punto dado usando spatial grid
     */
    _getPicAtPoint(globalPoint) {
      if (!this.picsContainer) return null;

      // Convertir coordenadas globales a locales del contenedor
      const local = this.picsContainer.toLocal(globalPoint);

      // Usar spatial grid para búsqueda eficiente O(1) en vez de O(n)
      const candidates = this.spatialOptimizer.getElementsFromGrid(
        local.x - 1, local.x + 1,
        local.y - 1, local.y + 1,
        this.picsContainer
      );

      // Encontrar el pic que contiene el punto
      for (const pic of candidates) {
        if (pic.renderable !== false && this._pointInPic(local, pic)) {
          return pic;
        }
      }
      return null;
    }

    /**
     * Verifica si un punto está dentro de un pic
     */
    _pointInPic(point, pic) {
      const x = pic.picX !== undefined ? pic.picX : pic.x;
      const y = pic.picY !== undefined ? pic.picY : pic.y;
      const width = pic.picWidth !== undefined ? pic.picWidth : pic.width;
      const height = pic.picHeight !== undefined ? pic.picHeight : pic.height;

      return point.x >= x &&
             point.x <= x + width &&
             point.y >= y &&
             point.y <= y + height;
    }

    /**
     * Handler para movimiento del puntero - hover via event delegation
     * Para PicSprite: usa applyHover/removeHover
     * Para Pic (Graphics): simula pointerover/pointerout solo si _useSprites está activo
     *   (si no, Pic maneja su propio hover con listeners individuales)
     */
    _onPicsPointerMove(globalPoint) {
      if (!this._useSprites) return; // Pic maneja hover con sus propios listeners

      const pic = this._getPicAtPoint(globalPoint);

      // Si el hover no cambió, no hacer nada
      if (pic === this._hoveredPic) return;

      // Quitar hover del anterior inmediatamente
      if (this._hoveredPic && typeof this._hoveredPic.removeHover === 'function') {
        this._hoveredPic.removeHover();
      }

      // Aplicar hover al nuevo (si hay uno)
      if (pic && typeof pic.applyHover === 'function') {
        pic.applyHover();
      }

      this._hoveredPic = pic; // null si el mouse no está sobre ningún pic
    }

    /**
     * Handler para click/tap - gestiona selección y doble click
     */
    _onPicsPointerDown(e) {
      const pic = this._getPicAtPoint(e.global);
      if (!pic) return;

      const now = Date.now();
      const isDoubleClick = (now - this._lastClickTime) < 300;
      this._lastClickTime = now;

      if (isDoubleClick) {
        // Doble click - toggle selección
        if (this._clickTimeout) {
          clearTimeout(this._clickTimeout);
          this._clickTimeout = null;
        }
        this._handlePicDoubleClick(pic, e);
      } else {
        // Single click con delay para distinguir de doble click
        this._clickTimeout = setTimeout(() => {
          this._handlePicSingleClick(pic, e);
          this._clickTimeout = null;
        }, 300);
      }
    }

    /**
     * Maneja single click - muestra info del pic
     */
    _handlePicSingleClick(pic, e) {
      document.dispatchEvent(new CustomEvent('__epc_pic_show_info', {
        detail: {
          pic: {
            attrs: {
              id: pic.picId,
              name: pic.picName,
              type: pic.picType,
              layerName: pic.picLayerName,
              status: pic.picStatus,
              statusColor: pic.picStatusColor,
              previouStatus: pic.picPreviouStatus,
              incident: pic.picIncident,
              incidentColor: pic.picIncidentColor,
              previouIncident: pic.picPreviouIncident,
            },
          },
          x: e.global.x,
          y: e.global.y,
        }
      }));
    }

    /**
     * Maneja doble click - toggle selección
     */
    _handlePicDoubleClick(pic, e) {
      // Si está en modo visualización de incidencias, no permitir selección
      if (pic.picShowOperationalStatuses) {
        document.dispatchEvent(new CustomEvent('__epc_pic_incident_view_mode_active', { detail: false }));
        return;
      }

      pic.SelectPic(pic);
    }

    /**
     * Viewport Culling optimizado: solo renderiza elementos visibles en pantalla
     * Reduce dramáticamente los draw calls al ocultar elementos fuera del viewport
     */
    _performCulling() {
      if (!this.picsContainer || this.picsContainer.children.length === 0) return;

      const renderer = this.app.renderer;

      // Usar toLocal para convertir las esquinas de la pantalla a coordenadas del picsContainer
      // Esto maneja correctamente todas las transformaciones (scale, position, rotation)
      const topLeft = this.picsContainer.toLocal(new Point(0, 0));
      const bottomRight = this.picsContainer.toLocal(new Point(renderer.width, renderer.height));

      // Calcular bounds del viewport en coordenadas locales del picsContainer
      const viewBounds = {
        minX: Math.min(topLeft.x, bottomRight.x),
        maxX: Math.max(topLeft.x, bottomRight.x),
        minY: Math.min(topLeft.y, bottomRight.y),
        maxY: Math.max(topLeft.y, bottomRight.y)
      };

      // Margen para pre-cargar elementos cercanos (evita pop-in visual)
      const margin = 100;
      viewBounds.minX -= margin;
      viewBounds.maxX += margin;
      viewBounds.minY -= margin;
      viewBounds.maxY += margin;

      // Contadores para verificar culling
      let visibleCount = 0;
      const totalCount = this.picsContainer.children.length;

      // Iterar sobre los hijos y determinar visibilidad
      for (const child of this.picsContainer.children) {
        // Usar bounds pre-calculados del SpatialOptimizer si existen
        if (!child._boundsCached && this.spatialOptimizer) {
          this.spatialOptimizer.preCalculateBounds(child);
        }

        // Determinar si el elemento está dentro del viewport
        const isVisible = !(
          child._maxX < viewBounds.minX ||
          child._minX > viewBounds.maxX ||
          child._maxY < viewBounds.minY ||
          child._minY > viewBounds.maxY
        );

        child.renderable = isVisible;
        // Sincronizar visibilidad del borde dinámico (si existe)
        if (child._border) {
          child._border.renderable = isVisible;
        }
        if (isVisible) visibleCount++;
      }

      // Guardar estadísticas de culling para acceso externo
      this.cullingStats = {
        visible: visibleCount,
        total: totalCount,
        culled: totalCount - visibleCount
      };
    }

    // Manejador de eventos para clic del ratón
    // ¿Debe el arrastre mover la vista (pan)?
    //  - dragging=true  → modo pan explícito (botón de la barra) → siempre pan.
    //  - !selecting      → modo normal → pan directo con solo arrastrar (sin activar
    //                      ningún botón), igual que el visor Three.js del LayoutBuilder.
    //  - selecting=true && !dragging → modo selección/registro → NO pan (dibuja caja).
    // El pan solo desplaza si hay movimiento real, por lo que el click simple sobre
    // una entidad (abrir popover) sigue funcionando intacto.
    _canPan() {
      return this.dragging || !this.selecting;
    }

    _onMouseDown2(event) {

      if( this.selecting ) {
        // Guardar las coordenadas del clic inicial
        this.selectionBoxStartX = event.data.global.x;
        this.selectionBoxStartY = event.data.global.y;

        // Inicializar las coordenadas finales con las mismas que las iniciales
        // para evitar usar valores residuales de selecciones anteriores
        this.selectionBoxEndX = this.selectionBoxStartX;
        this.selectionBoxEndY = this.selectionBoxStartY;

        // Resetear el flag de arrastre - aún no se está arrastrando
        this.isSelectingDragging = false;

        // NO mostrar el boxSelect inicialmente - solo se mostrará cuando haya movimiento
        // Esto evita que aparezca en clicks simples sin arrastre
        // Limpiar todas las propiedades del boxSelect para evitar que aparezca con valores residuales
        this.boxSelect.style.display = 'none'
        this.boxSelect.style.left = '0px'
        this.boxSelect.style.top = '0px'
        this.boxSelect.style.width = '0px'
        this.boxSelect.style.height = '0px'

        this.boxSelect.style.backgroundColor = 'rgba(12,94,149, 0.7)' //'#0c5e95'

        // Verificar si las coordenadas están dentro del área seleccionable
        //   if (selectableArea.containsPoint(new PIXI.Point(x, y))) {
        //     // Cambiar el color del área seleccionable (por ejemplo, a verde)
        //     selectableArea.tint = 0x00FF00;
        // } else {
        //     // Restaurar el color original del área seleccionable (azul)
        //     selectableArea.tint = 0x0000FF;
        // }

        // Escuchar eventos de movimiento del ratón y liberación del clic
      } else {
        // // Coordenadas iniciales (posición del ratón en coordenadas de pantalla)
        // this.selectionBoxStartX = event.data.global.x;
        // this.selectionBoxStartY = event.data.global.y;
        // // Crear el gráfico de selección si no existe y añadirlo al stage por encima del contenido
        // if (!this.selectionGraphics) {
        //     this.selectionGraphics = new Graphics();
        //     this.app.stage.addChild(this.selectionGraphics);
        // }
        // // Preparar el gráfico (color semitransparente y sin dibujo inicial)
        // this.selectionGraphics.clear();
        // this.selectionGraphics.lineStyle(1, 0x0C5E95, 1);            // borde azul
        // this.selectionGraphics.beginFill(0x0C5E95, 0.7);             // relleno azul translúcido (70% opacidad)
        // this.selectionGraphics.drawRect(this.selectionBoxStartX,     // dibujar rect inicial vacío
        //                                 this.selectionBoxStartY,
        //                                 0, 0);
        // this.selectionGraphics.endFill();
      }

      if( this._canPan() ) {
        this.dragData = event.data
        this.dragData.originalPosition = event.data.getLocalPosition(this.globalContainer.parent);
        this.dragData.originalPosition.x -= this.globalContainer.x;
        this.dragData.originalPosition.y -= this.globalContainer.y;
      }

      this.app.stage.on('pointerup', this._onMouseUp2);
      this.app.stage.on('pointermove', this._onMouseMove2);
    }

    // Manejador de eventos para movimiento del ratón
    _onMouseMove2(event) {

      if(this.selecting) {
        // VERIFICACIÓN CRÍTICA: Solo procesar si el botón está presionado
        // event.data.buttons === 1 significa botón izquierdo presionado
        // Si el botón no está presionado, no actualizar coordenadas para evitar
        // que aparezca el cuadro azul cuando solo se mueve el mouse sin arrastrar
        if (event.data.buttons !== 1) {
          // El botón no está presionado, no actualizar coordenadas
          return;
        }

        // Calcular el tamaño del área de selección
        this.selectionBoxEndX = event.data.global.x
        this.selectionBoxEndY = event.data.global.y

        // Normalizar coordenadas para que funcione en ambas direcciones
        const rectX = Math.min(this.selectionBoxStartX, this.selectionBoxEndX);
        const rectY = Math.min(this.selectionBoxStartY, this.selectionBoxEndY);
        const rectWidth = Math.abs(this.selectionBoxEndX - this.selectionBoxStartX);
        const rectHeight = Math.abs(this.selectionBoxEndY - this.selectionBoxStartY);

        // Solo mostrar el boxSelect si hay movimiento significativo (área mínima de 5x5 píxeles)
        // Esto evita que aparezca en clicks simples sin arrastre
        const MIN_SELECTION_SIZE = 5;
        if (rectWidth >= MIN_SELECTION_SIZE || rectHeight >= MIN_SELECTION_SIZE) {
          // Marcar que realmente se está arrastrando
          this.isSelectingDragging = true;

          // Actualizar posición y tamaño del boxSelect
          this.boxSelect.style.left = `${rectX}px`
          this.boxSelect.style.top = `${rectY}px`
          this.boxSelect.style.width = `${rectWidth}px`
          this.boxSelect.style.height = `${rectHeight}px`
          this.boxSelect.style.display = 'block'
        } else {
          // Si el área es muy pequeña, NUNCA establecer left/top
          // CRÍTICO: Establecer width/height a 0 ANTES de ocultar
          // para evitar que aparezca desde el origen con dimensiones residuales
          this.boxSelect.style.width = '0px'
          this.boxSelect.style.height = '0px'
          this.boxSelect.style.display = 'none'
          // IMPORTANTE: NO establecer left/top aquí para evitar que aparezca desde el origen
        }

        // Guardar valores normalizados para uso posterior
        this.selectionBoxWidth = rectWidth;
        this.selectionBoxHeight = rectHeight;
      } else {
        // // Actualizar las coordenadas finales mientras se arrastra
        // this.selectionBoxEndX = event.data.global.x;
        // this.selectionBoxEndY = event.data.global.y;
        // // Calcular posición y tamaño del rectángulo (considerando arrastre en cualquier dirección)
        // const rectX = Math.min(this.selectionBoxStartX, this.selectionBoxEndX);
        // const rectY = Math.min(this.selectionBoxStartY, this.selectionBoxEndY);
        // const rectWidth  = Math.abs(this.selectionBoxEndX - this.selectionBoxStartX);
        // const rectHeight = Math.abs(this.selectionBoxEndY - this.selectionBoxStartY);
        // // Redibujar el rectángulo de selección con las nuevas dimensiones
        // this.selectionGraphics.clear();
        // this.selectionGraphics.lineStyle(1, 0x0C5E95, 1);
        // this.selectionGraphics.beginFill(0x0C5E95, 0.7);
        // this.selectionGraphics.drawRect(rectX, rectY, rectWidth, rectHeight);
        // this.selectionGraphics.endFill();
      }

      if( this._canPan() && this.dragData ) {
          const newPosition = this.dragData.getLocalPosition(this.globalContainer.parent);
          this.globalContainer.x = newPosition.x - this.dragData.originalPosition.x;
          this.globalContainer.y = newPosition.y - this.dragData.originalPosition.y;

          // Emitir evento de cambio de viewport para sincronización con Google Maps
          this._emitViewportChanged();
      }
    }

    // Manejador de eventos para liberación del clic
    _onMouseUp2(e) {

      if (this.selecting) {
        // Detener de escuchar eventos de movimiento del ratón y liberación del clic
        let self = this

        // Calcular el tamaño del área de selección antes de normalizar
        const selectionWidth = Math.abs(this.selectionBoxEndX - this.selectionBoxStartX);
        const selectionHeight = Math.abs(this.selectionBoxEndY - this.selectionBoxStartY);

        // Verificar si hubo movimiento significativo (área mínima de 5x5 píxeles)
        // Si no hay movimiento, es un click simple y no procesamos la selección
        const MIN_SELECTION_SIZE = 5;
        if (selectionWidth < MIN_SELECTION_SIZE && selectionHeight < MIN_SELECTION_SIZE) {
          // Click simple sin arrastre - limpiar y salir sin procesar selección
          this.selectionBoxStartX = 0
          this.selectionBoxStartY = 0
          this.selectionBoxEndX = 0
          this.selectionBoxEndY = 0
          this.selectionBoxHeight = 0
          this.selectionBoxWidth = 0
          this.isSelectingDragging = false

          // Limpiar completamente el boxSelect para evitar que aparezca
          // CRÍTICO: Asegurar que width y height estén en 0 antes de ocultar
          // para evitar que aparezca desde el origen con dimensiones residuales
          this.boxSelect.style.width = '0px'
          this.boxSelect.style.height = '0px'
          this.boxSelect.style.display = 'none'
          this.boxSelect.style.left = '0px'
          this.boxSelect.style.top = '0px'
          return;
        }

        // Normalizar coordenadas para que funcione en ambas direcciones
        const normalizedStartX = Math.min(this.selectionBoxStartX, this.selectionBoxEndX);
        const normalizedStartY = Math.min(this.selectionBoxStartY, this.selectionBoxEndY);
        const normalizedEndX = Math.max(this.selectionBoxStartX, this.selectionBoxEndX);
        const normalizedEndY = Math.max(this.selectionBoxStartY, this.selectionBoxEndY);

        // Convertir a coordenadas locales del contenedor
        let pointStart = this.picsContainer.toLocal(new Point(normalizedStartX, normalizedStartY))
        let pointEnd = this.picsContainer.toLocal(new Point(normalizedEndX, normalizedEndY))

        // Normalizar para asegurar que pointStart < pointEnd
        const selMinX = Math.min(pointStart.x, pointEnd.x)
        const selMaxX = Math.max(pointStart.x, pointEnd.x)
        const selMinY = Math.min(pointStart.y, pointEnd.y)
        const selMaxY = Math.max(pointStart.y, pointEnd.y)

        // OPTIMIZACIÓN 1: Viewport Culling - Solo procesar elementos visibles
        const viewportBounds = this.spatialOptimizer.getViewportBounds(
          this.viewport,
          this.picsContainer,
          this.app
        )

        // OPTIMIZACIÓN 2: Spatial Grid - Obtener solo candidatos del grid
        const candidates = this.spatialOptimizer.getElementsFromGrid(
          selMinX,
          selMaxX,
          selMinY,
          selMaxY,
          this.picsContainer
        )

        // OPTIMIZACIÓN 3: Filtrar usando bounds pre-calculados y condiciones combinadas
        let elementsToSelect = []

        // Usar un solo loop optimizado en lugar de múltiples filters
        for (const el of candidates) {
          // Filtrar por tipo de capa si es necesario
          if (this.workLayer === 'nodes' && el.picType !== 'nodes') continue
          if (this.workLayer !== 'nodes' && el.picType === 'nodes') continue

          // Viewport culling: saltar elementos no visibles
          if (viewportBounds && !this.spatialOptimizer.isElementVisible(el, viewportBounds)) continue

          // Verificar intersección con área de selección usando bounds pre-calculados
          if (!this.spatialOptimizer.intersectsSelection(
            el,
            { x: selMinX, y: selMinY },
            { x: selMaxX, y: selMaxY }
          )) continue

          // Saltar elementos ya seleccionados
          if (el.picSelected) continue

          elementsToSelect.push(el)
        }

        // Procesar elementos en batches para no bloquear la UI
        // Con las optimizaciones, procesamos más elementos por batch ya que hay menos candidatos
        const BATCH_SIZE = 50;
        let currentIndex = 0;

        const processBatch = () => {
          const endIndex = Math.min(currentIndex + BATCH_SIZE, elementsToSelect.length);

          for (let i = currentIndex; i < endIndex; i++) {
            const el = elementsToSelect[i];
            if (el && !el.picSelected) {
              el.SelectPic(el);
            }
          }

          currentIndex = endIndex;

          // Si aún hay elementos por procesar, continuar en el siguiente frame
          if (currentIndex < elementsToSelect.length) {
            requestAnimationFrame(processBatch);
          }
        };

        // Iniciar el procesamiento por batches
        if (elementsToSelect.length > 0) {
          requestAnimationFrame(processBatch);
        }

        this.selectionBoxStartX = 0
        this.selectionBoxStartY = 0
        this.selectionBoxEndX = 0
        this.selectionBoxEndY = 0
        this.selectionBoxHeight = 0
        this.selectionBoxWidth = 0

        // Limpiar completamente el boxSelect después de procesar la selección
        this.boxSelect.style.display = 'none'
        this.boxSelect.style.left = '0px'
        this.boxSelect.style.top = '0px'
        this.boxSelect.style.width = '0px'
        this.boxSelect.style.height = '0px'


      } else {
        // // Convertir las esquinas del rectángulo de selección a coordenadas del mundo (contenido)
        // const startPoint = this.picsContainer.toLocal(new Point(this.selectionBoxStartX, this.selectionBoxStartY));
        // const endPoint   = this.picsContainer.toLocal(new Point(this.selectionBoxEndX, this.selectionBoxEndY));
        // const minX = Math.min(startPoint.x, endPoint.x);
        // const minY = Math.min(startPoint.y, endPoint.y);
        // const maxX = Math.max(startPoint.x, endPoint.x);
        // const maxY = Math.max(startPoint.y, endPoint.y);
        // const regionWidth  = maxX - minX;
        // const regionHeight = maxY - minY;
        // if (regionWidth > 0 && regionHeight > 0) {
        //     // Calcular nueva escala para encajar la región en la ventana
        //     const scaleX = this.canvasWidth  / regionWidth;
        //     const scaleY = this.canvasHeight / regionHeight;
        //     const newScale = Math.min(scaleX, scaleY);
        //     // Aplicar la nueva escala (zoom) utilizando el contenedor global
        //     this.zoomLevel = newScale;
        //     this.globalContainer.scale.set(this.zoomLevel, this.zoomLevel);
        //     // Calcular el centro de la región seleccionada en coordenadas de contenido
        //     const centerX = minX + regionWidth / 2;
        //     const centerY = minY + regionHeight / 2;
        //     // Reposicionar globalContainer de forma que centerX, centerY queden centrados en la pantalla
        //     this.globalContainer.x = this.canvasWidth  / 2 - (centerX + this.picsContainer.x) * this.zoomLevel;
        //     this.globalContainer.y = this.canvasHeight / 2 - (centerY + this.picsContainer.y) * this.zoomLevel;
        // }

        // // Limpiar el gráfico de selección de la pantalla
        //   if (this.selectionGraphics) {
        //     this.selectionGraphics.clear();
        // }
        // // Reiniciar las dimensiones almacenadas del rectángulo de selección
        // this.selectionBoxStartX = this.selectionBoxStartY = 0;
        // this.selectionBoxEndX   = this.selectionBoxEndY   = 0;
        // this.selectionBoxWidth  = this.selectionBoxHeight = 0;
        // // (Opcional) Finalizar el modo de selección de área de zoom
        // // this.selecting = false;
      }

      if(this._canPan() && this.dragData) {
        this.dragData = null
      }

      this.app.stage.off('pointermove', this._onMouseMove2);
      this.app.stage.off('pointerup', this._onMouseUp2);
    }

    // para zoom stage
    // TODO: SIRVE PARA ZOOM DE LA ESCENA CON PIXIJS
    ZoomStage(scaleFactor) {
      const global = this.globalContainer;
      const oldScale = global.scale.x;
      const newScale = oldScale * scaleFactor;
      // Punto de referencia: centro de la ventana de visualización
      const px = this.app.renderer.width / 2;
      const py = this.app.renderer.height / 2;
      // Coordenadas del punto de referencia en el sistema de la gráfica (antes de zoom)
      const worldX = (px - global.position.x) / oldScale;
      const worldY = (py - global.position.y) / oldScale;
      // Aplicar nueva escala
      global.scale.set(newScale, newScale);
      // Reposicionar contenedor para mantener el centro enfocado
      global.position.x = px - worldX * newScale;
      global.position.y = py - worldY * newScale;

      // Emitir evento de cambio de viewport para sincronización con Google Maps
      this._emitViewportChanged();
    }

    // Zoom anclado en un punto de pantalla (px, py) — misma fórmula que ZoomStage
    // pero manteniendo fijo el punto bajo el cursor (zoom-to-pointer para la rueda).
    ZoomStageAtPointer(scaleFactor, px, py) {
      const global = this.globalContainer;
      const oldScale = global.scale.x;
      const newScale = oldScale * scaleFactor;
      // Coordenadas del punto de anclaje en el sistema de la gráfica (antes del zoom)
      const worldX = (px - global.position.x) / oldScale;
      const worldY = (py - global.position.y) / oldScale;
      // Aplicar nueva escala
      global.scale.set(newScale, newScale);
      // Reposicionar contenedor para mantener el punto bajo el cursor fijo
      global.position.x = px - worldX * newScale;
      global.position.y = py - worldY * newScale;

      // Emitir evento de cambio de viewport para sincronización con Google Maps
      this._emitViewportChanged();
    }

    // Handler nativo de la rueda del mouse: zoom hacia el cursor.
    // preventDefault evita que la página haga scroll mientras se hace zoom.
    _onWheel(e) {
      e.preventDefault();
      const rect = this.app.canvas.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      // deltaY < 0 → rueda hacia arriba → acercar; > 0 → alejar
      const scaleFactor = e.deltaY < 0 ? 1.1 : 0.9;
      this.ZoomStageAtPointer(scaleFactor, px, py);
    }

    UpdateViewportCulling(viewport) {
      const viewportBounds = viewport.getBounds();

      this.picsContainer.children.forEach(object => {
          const objectBounds = object.getBounds();

          // Check if object is within viewport bounds
          const isVisible = objectBounds.x + objectBounds.width >= viewportBounds.x &&
                            objectBounds.x <= viewportBounds.x + viewportBounds.width &&
                            objectBounds.y + objectBounds.height >= viewportBounds.y &&
                            objectBounds.y <= viewportBounds.y + viewportBounds.height;

          // Set renderable property based on visibility
          object.renderable = isVisible;
      });
    }


    async TakeScreenshot(saveLocally = false, filename = null) {

        this.app.stop();

        // Guardar estado original del renderer y contenedores
        const originalWidth = this.app.renderer.width;
        const originalHeight = this.app.renderer.height;
        const originalResolution = this.app.renderer.resolution || 1;

        // Guardar estado del contenedor global (escala y posición)
        const originalScaleX = this.globalContainer.scale.x;
        const originalScaleY = this.globalContainer.scale.y;
        const originalPosX = this.globalContainer.position.x;
        const originalPosY = this.globalContainer.position.y;

        // Desactivar culling: hacer TODOS los sprites y bordes visibles para la captura
        for (const child of this.picsContainer.children) {
          child.renderable = true;
          if (child._border) {
            child._border.renderable = true;
          }
        }
        // Bordes estáticos (el Graphics combinado y bordes dinámicos en bordersContainer)
        if (this.bordersContainer) {
          for (const child of this.bordersContainer.children) {
            child.renderable = true;
          }
        }

        // Configurar para Full HD (1920x1080) con alta resolución
        const fullHDWidth = 1920;
        const fullHDHeight = 1080;
        const highResolution = 3; // Aumentar resolución para mejor calidad

        // Obtener límites del contenido actual
        const contentBounds = this.globalContainer.getLocalBounds();
        const contentWidth = contentBounds.width || originalWidth;
        const contentHeight = contentBounds.height || originalHeight;

        // Calcular la escala necesaria para ajustar el contenido a Full HD
        const scaleX = fullHDWidth / contentWidth;
        const scaleY = fullHDHeight / contentHeight;
        const targetScale = Math.min(scaleX, scaleY) * 0.9; // 90% para dejar márgenes

        // Redimensionar el renderer a Full HD con alta resolución
        this.app.renderer.resize(fullHDWidth, fullHDHeight, highResolution);

        // Aplicar escala temporal al contenido para que se ajuste a Full HD
        this.globalContainer.scale.set(targetScale, targetScale);

        // Centrar el contenido en el nuevo tamaño
        const scaledWidth = contentWidth * targetScale;
        const scaledHeight = contentHeight * targetScale;
        const offsetX = (fullHDWidth - scaledWidth) / 2 - contentBounds.x * targetScale;
        const offsetY = (fullHDHeight - scaledHeight) / 2 - contentBounds.y * targetScale;
        this.globalContainer.position.set(offsetX, offsetY);

        // Forzar renderizado antes de capturar
        this.app.renderer.render(this.app.stage);

        // Capturar con clearColor blanco directamente en extract (la forma correcta en PixiJS v8)
        const url = await this.app.renderer.extract.base64({
          target: this.app.stage,
          format: 'png',
          clearColor: '#ffffff',
          resolution: highResolution,
        });

        const screenshot = this.Base64ToBlob(url.replace('data:image/png;base64,', ''), 'image/png')

        // Guardar localmente si se solicita
        if (saveLocally) {
            this._saveImageLocally(url, filename);
        }

        // Restaurar estado original del renderer
        this.app.renderer.resize(originalWidth, originalHeight, originalResolution);

        // Restaurar estado original del contenedor global
        this.globalContainer.scale.set(originalScaleX, originalScaleY);
        this.globalContainer.position.set(originalPosX, originalPosY);

        this.app.start();

        // El ticker re-ejecutará _performCulling() automáticamente en el siguiente frame

        return screenshot
    }

    _saveImageLocally(dataUrl, filename = null) {
        try {
            // Generar nombre de archivo si no se proporciona
            if (!filename) {
                const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
                filename = `screenshot-${timestamp}.png`;
            }

            // Crear un enlace temporal para descargar
            const link = document.createElement('a');
            link.href = dataUrl;
            link.download = filename;
            link.style.display = 'none';

            // Agregar al DOM, hacer clic y remover
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            console.log(`Imagen guardada localmente: ${filename}`);
        } catch (error) {
            console.error('Error al guardar la imagen localmente:', error);
        }
    }

    _getBase64Image(url) {
      const img = document.createElement("img");
      return new Promise((resolve, reject)=>{
        img.src = url,
        img.addEventListener("load", ()=>{
            const canva = document.createElement("canvas")
            canva.width = img.width,
            canva.height = img.height;
            canva.getContext("2d").drawImage(img, 0, 0, img.width, img.height);
            resolve(canva.toDataURL("image/png"))
        })

        img.addEventListener("error", ()=>{
            reject()
        })
      })
    }


    Base64ToBlob(base64String, contentType) {
      contentType = contentType || '';
      var sliceSize = 1024;
      var byteCharacters = atob(base64String);
      var byteArrays = [];

      for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        var slice = byteCharacters.slice(offset, offset + sliceSize);
        var byteNumbers = new Array(slice.length);

        for (var i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        var byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, { type: contentType });
    }















    _SetSize(width, height) {
      this.stage.width = width
      this.stage.height = height
    }

    SetScene(scene) {
      this.entities = scene.entities
      this.labels = scene.texts
      this.bounds = scene.bounds
      this.origin = scene.origin
      this.layers = scene.layers
    }

    SetSize(width, height) {
      this._EnsureRenderer()

      const hScale = width / this.canvasWidth
      const vScale = height / this.canvasHeight

      const cam = this.camera
      const centerX = (cam.left + cam.right) / 2
      const centerY = (cam.bottom + cam.top) / 2
      const camWidth = cam.right - cam.left
      const camHeight = cam.top - cam.bottom
      cam.left = centerX - hScale * camWidth / 2
      cam.right = centerX + hScale * camWidth / 2
      cam.bottom = centerY - vScale * camHeight / 2
      cam.top = centerY + vScale * camHeight / 2
      cam.updateProjectionMatrix()

      this.canvasWidth = width
      this.canvasHeight = height
      this.renderer.setSize(width, height)

      this._Emit("resized", {width, height})
      this._Emit("viewChanged")
      this.Render()
    }

    /** @return {Iterable<{name:String, color:number}>} List of layer names. */
    GetLayers() {
      if (this.layers === undefined) return []

      const result = []
      for (const lyr of this.layers.values()) {
          result.push({
              name: lyr.name,
              displayName: lyr.displayName,
              color: this._TransformColor(lyr.color)
          })
      }
      return result
    }

    ShowLayer(name, show) {
        this._EnsureRenderer()
        const layer = this.layers.get(name)
        if (!layer) {
            return
        }
        for (const obj of layer.objects) {
            obj.visible = show
        }
        this.Render()
    }

    /** Reset the viewer state. */
    Clear() {
        // this._EnsureRenderer()
        // if (this.worker) {
        //     this.worker.Destroy(true)
        //     this.worker = null
        // }
        // if (this.controls) {
        //     this.controls.dispose()
        //     this.controls = null
        // }
        // this.scene.clear()
        // for (const layer of this.layers.values()) {
        //     layer.Dispose()
        // }
        // this.layers.clear()
        // this.blocks.clear()
        // this.materials.each(e => e.material.dispose())
        // this.materials.clear()
        // this.SetView({x: 0, y: 0}, 2)
        // this._Emit("cleared")
        // this.Render()
    }

    /** Free all resources. The viewer object should not be used after this method was called. */
    Destroy() {
      // Evitar doble destrucción
      if (this._destroyed) return;
      this._destroyed = true;

      // 1. Remover listeners globales
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }

      if (this._beforeUnloadHandler) {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler);
        this._beforeUnloadHandler = null;
      }

      // Limpiar timeout de click si existe
      if (this._clickTimeout) {
        clearTimeout(this._clickTimeout);
        this._clickTimeout = null;
      }

      // Limpiar comprobación pendiente del modo de línea (bordes híbridos)
      if (this._linesModeTimeout) {
        clearTimeout(this._linesModeTimeout);
        this._linesModeTimeout = null;
      }

      // 2. Limpiar eventos de cada Pic individualmente (sin destruir - lo hará app.destroy)
      if (this.entities && this.entities.length > 0) {
        for (const pic of this.entities) {
          if (pic && pic.off) {
            pic.off('pointerover');
            pic.off('pointerout');
          }
        }
        this.entities.length = 0;
      }

      // 3. Remover event delegation listeners
      if (this.app && this.app.canvas && this._canvasMouseMove) {
        this.app.canvas.removeEventListener('mousemove', this._canvasMouseMove);
      }
      if (this.app && this.app.canvas && this._onWheel) {
        this.app.canvas.removeEventListener('wheel', this._onWheel);
      }
      if (this.picsContainer) {
        this.picsContainer.off('pointerdown', this._onPicsPointerDown);
      }

      if (this.app && this.app.stage) {
        this.app.stage.off('pointerdown', this._onMouseDown2);
        this.app.stage.off('pointerup', this._onMouseUp2);
        this.app.stage.off('pointermove', this._onMouseMove2);
      }

      // 4. Limpiar spatial optimizer
      if (this.spatialOptimizer) {
        this.spatialOptimizer.clear();
        this.spatialOptimizer = null;
      }

      // 5. Detener y destruir la aplicación Pixi
      // app.destroy(true) destruye todo: stage, renderer, contenedores e hijos
      if (this.app) {
        if (this.app.ticker) {
          this.app.ticker.stop();
        }
        this.app.stop();

        // Destruir la aplicación (esto destruye stage, renderer y todos los hijos)
        try {
          this.app.destroy(true, { children: true, texture: true, baseTexture: true });
        } catch (e) {
          // Ignorar errores de destrucción si ya fue destruido
          console.warn('PixiViewer: Error en destroy (puede ignorarse):', e.message);
        }
      }

      // 8. Limpiar todas las referencias
      this.app = null;
      this.globalContainer = null;
      this.picsContainer = null;
      this.backsContainer = null;
      this.bordersContainer = null;
      this.labelsContainer = null;
      this.entities = [];
      this.labels = [];
      this.bounds = null;
      this.origin = null;
      this.layers = [];
      this._hoveredPic = null;
      this.viewport = null;
      this.boxSelect = null;
      this.cullingStats = null;
      this._labelsCreated = false;
      this._backgroundsGraphics = null;
    }

    SetView() {
      // Dimensiones de la ventana de visualización en PixiJS
      const windowWidth = this.canvasWidth /* ancho de la ventana en píxeles */;
      const windowHeight = this.canvasHeight /* alto de la ventana en píxeles */;
      let offset = this._GetOffset()

      // Dimensiones del diseño en AutoCAD
      const autocadWidth = this.bounds ? this.bounds.maxX - this.bounds.minX + offset.x : windowWidth;
      const autocadHeight = this.bounds ? this.bounds.maxY - this.bounds.minY  + offset.y : windowHeight;

      // Calcular la escala en X y en Y
      const scaleX = windowWidth / autocadWidth;
      const scaleY = windowHeight / autocadHeight;

      // Usar la escala más pequeña para mantener la relación de aspecto
      const scale = Math.min(scaleX, scaleY);

      this.backsContainer.scale.set(scale);
      this.picsContainer.scale.set(scale);
      this.bordersContainer.scale.set(scale);
      this.labelsContainer.scale.set(scale);

      const posX = (this.app.renderer.width - this.picsContainer.width) / 2;
      const posY = (this.app.renderer.height - this.picsContainer.height) / 2;

      this.backsContainer.position.set(posX, posY);
      this.picsContainer.position.set(posX, posY);
      this.bordersContainer.position.set(posX, posY);
      this.labelsContainer.position.set(posX, posY);
    }

    EnableDraggable(enable) {
      this.stage.draggable(enable)
    }

    /** Set view to fit the specified bounds. */
    FitView(padding = 0.1) {

      // // Dimensiones de la ventana de visualización en PixiJS
      // const windowWidth = this.app.renderer.width;
      // const windowHeight = this.app.renderer.height;

      // // Calcular la escala necesaria para que los límites de la escena se ajusten a la ventana de visualización
      // const scaleX = windowWidth / (this.bounds.maxX - this.bounds.minX);
      // const scaleY = windowHeight / (this.bounds.maxY - this.bounds.minY);
      // const scale = Math.min(scaleX, scaleY); // Usar la escala más pequeña para mantener la relación de aspecto

      // // Calcular la posición necesaria para centrar la escena en la ventana de visualización
      // const offsetX = (windowWidth - (this.bounds.maxX - this.bounds.minX) * scale) / 2 - this.bounds.minX * scale;
      // const offsetY = (windowHeight - (this.bounds.maxY - this.bounds.minY) * scale) / 2 - this.bounds.minY * scale;

      // // Aplicar la escala y la posición al contenedor principal o a la cámara
      // this.app.stage.scale.set(scale);
      // this.picsContainer.position.set(offsetX, offsetY);



      // 1. Restablecer transformaciones para medir límites reales del contenido
      this.globalContainer.scale.set(1);
      this.picsContainer.scale.set(1);
      this.backsContainer.scale.set(1);
      this.bordersContainer.scale.set(1);
      this.labelsContainer.scale.set(1);
      // Nota: No es necesario resetear this.globalContainer.position,
      // pues getLocalBounds calcula límites en coordenadas locales del contenedor.

      // 2. Obtener los límites actuales del contenido dentro del contenedor global
      const contentBounds = this.globalContainer.getLocalBounds();
      const contentWidth  = contentBounds.width;
      const contentHeight = contentBounds.height;
      if (contentWidth <= 0 || contentHeight <= 0) {
          return; // Si no hay contenido o dimensiones inválidas, no ajustar vista.
      }

      // 3. Calcular la escala óptima para encajar todo el contenido en el viewport
      //    (incluyendo un padding porcentual para dejar márgenes alrededor).
      const paddedWidth  = contentWidth  * (1 + padding);
      const paddedHeight = contentHeight * (1 + padding);
      const scaleX = this.app.renderer.width  / paddedWidth;
      const scaleY = this.app.renderer.height / paddedHeight;
      const scale  = Math.min(scaleX, scaleY);  // Escala uniforme (mantener aspecto)

      // 4. Aplicar la escala calculada al contenedor global (escala uniforme en ambos ejes)
      this.globalContainer.scale.set(scale);

      // 5. Calcular desplazamiento necesario para centrar el contenido en el canvas.
      //    Se centra el bounding box del contenido restando la posición mínima (contentBounds.x/y)
      //    y dejando márgenes iguales a ambos lados.
      const offsetX = (this.app.renderer.width  - contentWidth * scale) / 2 - contentBounds.x * scale;
      const offsetY = (this.app.renderer.height - contentHeight * scale) / 2 - contentBounds.y * scale;

      // 6. Posicionar el contenedor global usando el desplazamiento calculado.
      this.globalContainer.position.set(offsetX, offsetY);
    }

    FitViewKonvaNew(padding = 0.1) {
      const container = this.domContainer;

      // Calcula el tamaño del contenedor
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      const stageWidth = this.stage.width()
      const stageHeight = this.stage.height()

      // Calcula la escala para ajustar el Stage al contenedor
      const scaleX = containerWidth / stageWidth;
      const scaleY = containerHeight / stageHeight;
      const scale = Math.min(scaleX, scaleY);

      // Escala el Stage y actualiza su tamaño
      this.stage.width(stageWidth * scale);
      this.stage.height(stageHeight * scale);

      // Centra el Stage en el contenedor
      this.stage.scale({ x: scale, y: scale });
      this.stage.offset({
        x: (stageWidth - stageWidth * scale) / 2,
        y: (stageHeight - stageHeight * scale) / 2,
      });

      // Dibujar los elementos del Stage actualizados
      this.stage.draw();
    }

    FitViewKonva(padding = 0.1) {

      // now we need to fit stage into parent container
      var containerWidth = this.domContainer.offsetWidth;
      var containerHeight = this.domContainer.offsetHeight;

      // but we also make the full scene visible
      // so we need to scale all objects on canvas
      var scaleX = containerWidth / this.stage.width();
      var scaleY = containerHeight / this.stage.height();
      // let scaleX = this.canvaEntities.width / this.stage.width();
      // let scaleY = this.canvaEntities.height / this.stage.height();
      const scale = Math.min(scaleX, scaleY);

      this.stage.width(this.stage.width() * scale);
      this.stage.height(this.stage.height() * scale);

      // this.stage.width(this.stage.width() * scaleX);
      // this.stage.height(this.stage.height() * scaleY);


      this.stage.scale({ x: scaleX, y: scaleY });

      let center = this._FitViewKonva(this.stage.width(), this.stage.height())
      let offset = this._GetOffset()
      // this.stage.position({ x: Math.abs(center.x + offset.x), y: Math.abs(center.y - offset.y) })
      this.stage.position({ x: Math.abs(center.x + offset.x), y: Math.abs(center.y) })
      // this.stage.position({ x: center.x, y: center.y })
      this.stage.draw()


      // this.ZoomStage(0.8) // ultimo recurso harcodeado
    }

    _FitViewKonva(canvasWidth, canvasHeight, padding = 0.1) {
      let minX = this.bounds.minX - this.origin.x
      let maxX = this.bounds.maxX - this.origin.x
      let minY = this.bounds.minY - this.origin.y
      let maxY = this.bounds.maxY - this.origin.y

      const aspect = canvasWidth / canvasHeight
      let width = maxX - minX
      const height = maxY - minY
      const center = {x: minX + width / 2, y: minY + height / 2}

      if (height * aspect > width) {
          width = height * aspect
      }

      if (width <= Number.MIN_VALUE * 2) {
          width = 1
      }

      return center
      // this.SetView(center, width * (1 + padding))
    }

    GetScale() {
      let canvasWidth = this.stage.width()
      let canvasHeight = this.stage.height()

      return {
        x: Math.round(canvasWidth / ((this.bounds.maxX  - this.origin.x) - (this.bounds.minX  - this.origin.x))),
        y: Math.round(canvasHeight / ((this.bounds.maxY  - this.origin.y) - (this.bounds.minY  - this.origin.y)))
      }
    }

    SetScaleStage() {
      let scale = this.GetScale()
      this.stage.scaleX (scale.x)
      this.stage.scaleY (scale.y)
    }

    /** @return {Vector2} Scene origin in global drawing coordinates. */
    GetOrigin() {
        return this.origin
    }

    // Este metodo debe recibir los layer que son controlados por nosotros para determinar el color del estado
    // Versión asíncrona que procesa en chunks para no bloquear el hilo principal
    async GetEntities(layers, typeRegister, onProgress = null) {
      let labels = []
      let entities = [];
      let backgrounds = []
      let completed = []

      // Datos para crear backgrounds en una sola textura
      const backgroundsData = [];

      let offset = this._GetOffset();
      const renderer = this.app.renderer;
      const entityKeys = Object.keys(this.entities);
      const total = entityKeys.length;
      const CHUNK_SIZE = 50; // Reducido de 200 a 50 para menos bloqueo

      for (let i = 0; i < total; i += CHUNK_SIZE) {
        const chunkEnd = Math.min(i + CHUNK_SIZE, total);

        // Procesar chunk
        for (let j = i; j < chunkEnd; j++) {
          const key = entityKeys[j];
          const element = this.entities[key];

          if (element) {
            if (element.type === 'structures') {
              // Recolectar datos de backgrounds para renderizar en una sola textura
              if (typeRegister !== 'issue') {
                backgroundsData.push({
                  x: Math.abs(element.x + offset.x),
                  y: Math.abs(element.y - offset.y),
                  width: element.width,
                  height: element.height,
                  fill: j % 2 === 0 ? '#f7f3f3' : null
                });
              }

              let picComplete = new PicComplete(element, offset, layers, j)
              this.backsContainer.addChild(picComplete)
              completed.push(picComplete)

              // Crear labels directamente (con resolución reducida en PicLabel)
              this.labelsContainer.addChild(new PicLabel(element, offset, j));
            }

            // Usar PicSprite (optimizado) en lugar de Pic (Graphics)
            if (this._useSprites) {
              entities.push(new PicSprite(element, offset, layers, this.layers, j, typeRegister, renderer))
            } else {
              entities.push(new Pic(element, offset, layers, this.layers, j, typeRegister))
            }
          }
        }

        // Reportar progreso
        if (onProgress) {
          onProgress(chunkEnd, total);
        }

        // Ceder control al navegador entre chunks
        if (i + CHUNK_SIZE < total) {
          await new Promise(resolve => requestAnimationFrame(resolve));
        }
      }

      this.entities = entities

      // Crear una sola textura con todos los backgrounds (optimización)
      if (backgroundsData.length > 0) {
        this._createBackgroundsTexture(backgroundsData);
      }

      return { labels, entities, backgrounds, completed }
    }

    /**
     * Crea una sola textura con todos los backgrounds
     * Reduce miles de draw calls a 1 solo
     */
    _createBackgroundsTexture(backgroundsData) {
      if (!backgroundsData || backgroundsData.length === 0) return;

      // Crear un Graphics temporal con todos los rectángulos
      const tempGraphics = new Graphics();

      for (const bg of backgroundsData) {
        if (bg.fill) {
          tempGraphics.rect(bg.x, bg.y, bg.width, bg.height).fill(bg.fill);
        }
      }

      // Desactivar eventos - los fondos no necesitan interacción
      tempGraphics.eventMode = 'none';

      // Agregar el Graphics combinado al inicio del backsContainer
      // (para que esté detrás de los demás elementos)
      if (this.backsContainer.children.length > 0) {
        this.backsContainer.addChildAt(tempGraphics, 0);
      } else {
        this.backsContainer.addChild(tempGraphics);
      }

      // Guardar referencia para poder destruirlo después
      this._backgroundsGraphics = tempGraphics;
    }

    /**
     * Crea bordes en bordersContainer separados de los sprites para batching óptimo.
     * - Bordes estáticos (sin incidencia): 1 solo Graphics combinado = 1 draw call
     * - Bordes dinámicos (con incidencia): Graphics individuales que se redibujan al cambiar
     */
    /**
     * Crea bordes en bordersContainer separados de los sprites.
     * Solo crea bordes dinámicos (con incidencia) por ahora.
     * Los bordes estáticos están desactivados temporalmente.
     * @param {Array} entities - entidades filtradas por tipo (del Render)
     */
    _createBordersForEntities(entities) {
      if (!this.bordersContainer) return;

      // Limpiar bordes anteriores y referencias dinámicas
      // (se destruyen para no acumular geometría al redibujar por cambio de zoom)
      const oldBorders = this.bordersContainer.removeChildren();
      for (const g of oldBorders) {
        if (g._pic && g._pic._border === g) g._pic._border = null;
        g.destroy();
      }

      // Modo de línea según el zoom actual (1px fijo en pantalla si estamos lejos)
      const pixelLine = this._shouldUsePixelLines();
      this._bordersPixelLine = pixelLine;
      this.bordersContainer._pixelLine = pixelLine;

      // 1 Graphics combinado para todos los bordes estáticos (sin incidencia)
      // Agrupar por color+ancho para minimizar cambios de estado en GPU
      const staticBorders = new Graphics();
      staticBorders.eventMode = 'none';
      let hasStaticBorders = false;

      for (const pic of entities) {
        // Solo PicSprite tiene applyHover (Pic maneja su propio borde)
        if (typeof pic.applyHover !== 'function') continue;

        // Limpiar referencia dinámica anterior
        pic._border = null;
        // Para que el pic pueda crear su propio borde si cambia de estado en caliente
        pic._bordersContainer = this.bordersContainer;

        if (pic.picIncident) {
          // Borde dinámico: Graphics individual (necesita redibujado selectivo).
          // El pic crea su Graphics (vinculado, con bounds para culling) y lo dibuja
          // con el color de la incidencia y el modo de línea actual.
          pic._ensureOwnBorder();
          pic._drawBorder();
        } else {
          // Borde estático: acumular en el Graphics combinado (1 draw call para todos)
          // Color según estado: gris plomo si no tiene estado, color de capa si lo tiene
          const style = pic.getLineStyle(pixelLine);

          staticBorders.rect(pic.picX, pic.picY, pic.picWidth, pic.picHeight)
            .stroke({ width: style.width, color: style.color, alpha: style.alpha, pixelLine: style.pixelLine });

          hasStaticBorders = true;
        }
      }

      // Agregar el Graphics combinado de bordes estáticos (si hay)
      if (hasStaticBorders) {
        this.bordersContainer.addChildAt(staticBorders, 0);
      }
    }

    async GetEntitiesEvacuationLine(layers, onProgress = null) {
      let labels = []
      let entities = [];
      let backgrounds = []
      let completed = []

      let offset = this._GetOffset();
      const renderer = this.app.renderer;
      const entityKeys = Object.keys(this.entities);
      const total = entityKeys.length;
      const CHUNK_SIZE = 200;

      for (let i = 0; i < total; i += CHUNK_SIZE) {
        const chunkEnd = Math.min(i + CHUNK_SIZE, total);

        for (let j = i; j < chunkEnd; j++) {
          const key = entityKeys[j];
          const element = this.entities[key];

          if (element) {
            if (element.type === 'nodes') {
              entities.push(new PicNode(element, offset, layers, this.layers, j))
              this.labelsContainer.addChild(new PicLabel(element, offset, j))
            } else {
              if (this._useSprites) {
                entities.push(new PicSprite(element, offset, layers, this.layers, j, 'progress', renderer))
              } else {
                entities.push(new Pic(element, offset, layers, this.layers, j, 'progress'))
              }
            }
          }
        }

        if (onProgress) {
          onProgress(chunkEnd, total);
        }

        if (i + CHUNK_SIZE < total) {
          await new Promise(resolve => requestAnimationFrame(resolve));
        }
      }

      this.entities = entities

      return { labels, entities, backgrounds, completed }
    }

    Render(type, typePictogram='mechanical_installation') {
      // Invalidar cache antes de cambiar el contenido
      this._invalidateStaticCache();

      this.picsContainer.removeChildren()

      if ('mechanical_installation' === typePictogram) {
        this.entities.filter( el => el.picType == type )
          .forEach((el) => { this.picsContainer.addChild(el) } )
      } else {
        this.entities
          .forEach((el) => {
            if (el.picType !== type)
              el.interactive = false
            else
              el.interactive = true
            this.picsContainer.addChild(el)
          } )
      }

      // Crear bordes solo para las entidades del tipo actual (solo PicSprite)
      if (this._useSprites) {
        const renderedEntities = Array.from(this.picsContainer.children);
        this._createBordersForEntities(renderedEntities);
      }

      this.SetView()

      // Construir spatial grid después de renderizar
      this.spatialOptimizer.buildSpatialGrid(this.picsContainer)

      // Cachear contenedores estáticos para mejorar rendimiento
      this._cacheStaticContainers();

      // Ajustar vista para que el contenido ocupe el área visible
      this.FitToScreen();
    }

    /**
     * Cachea los contenedores estáticos (fondos, labels) como texturas
     * Reduce múltiples draw calls a uno solo para elementos que no cambian
     * NOTA: Desactivado temporalmente porque afecta la calidad del texto
     */
    _cacheStaticContainers() {
      // Deshabilitar interactividad del contenedor (esto sí ayuda al rendimiento)
      if (this.backsContainer) {
        this.backsContainer.interactiveChildren = false;
        this.backsContainer.eventMode = 'none';
      }

      // NOTA: El cacheAsTexture está desactivado porque degrada la calidad del texto
      // Los labels tienen resolución alta (8x) que se pierde al cachear
      // TODO: Considerar separar labels de backgrounds para poder cachear solo backgrounds
      /*
      if (this.backsContainer &&
          this.backsContainer.children.length > 0 &&
          !this._backsContainerCached) {
        try {
          this.backsContainer.cacheAsTexture(true);
          this._backsContainerCached = true;
        } catch (e) {
          console.warn('cacheAsTexture no disponible:', e.message);
        }
      }
      */
    }

    /**
     * Invalida el cache de contenedores estáticos
     * Llamar antes de modificar el contenido de los contenedores
     */
    _invalidateStaticCache() {
      if (this.backsContainer && this._backsContainerCached) {
        try {
          this.backsContainer.cacheAsTexture(false);
        } catch (e) {
          // Ignorar si no está disponible
        }
        this._backsContainerCached = false;
      }
    }

    GetEntitiesDesign(type) {
      return this.picsContainer.children.filter( el => el.picType == type )
    }

    _HandleWheel(e) {
      // Incrementar o disminuir el nivel de zoom en función de la dirección del scroll
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      this.zoomLevel += delta;

      // Limitar el nivel de zoom entre ciertos límites (opcional)
      this.zoomLevel = Math.max(0.1, Math.min(10, this.zoomLevel)); // Zoom mínimo: 0.1, Zoom máximo: 3

      // Aplicar el zoom
      this.applyZoom(this.zoomLevel);
    }

    // Función para aplicar el zoom
    applyZoom(scale) {
      // Escalar el contenido del lienzo (canvas)
      this.app.stage.scale.set(scale);
    }

    _GetNameEntity(entity, index) {
      if ( entity.name != null )
        return entity.name

      return 'E.' + entity.type.charAt(0).toUpperCase() + '.' + (index + 1)

    }

    _GetColoStatusEntityOnlyComplete(entity, layers) {
      if( entity.status != null ) {
        let lyr = layers.find( item => { return item.code === entity.type })
        if (lyr) {
          let idStatus = typeof entity.status === 'object' ? entity.status.id : entity.status;

          let status = lyr.statuses.find(item => { return item.id == idStatus && item.is_completion == 1})
          if (status)
            return status.color
        }
      }
     return null
   }

    _GetColorStatusOfLayout(entity, layers) {
       if( entity.status != null ) {
         let lyr = layers.find( item => { return item.code === entity.type })
         if (lyr) {
           let idStatus = typeof entity.status === 'object' ? entity.status.id : entity.status;

           let status = lyr.statuses.find(item => { return item.id == idStatus })
           if (status)
             return status.color
         }
       }
      return null

      // if ( typeof entity.status === 'object' )
      //   return entity.status.color
      // else {
      //   let lyr = layers.find( item => { return item.code === entity.type })
      //   if (lyr) {
      //     let status = lyr.statuses.find(item => { return item.id == entity.status })
      //     if (status) return status.color
      //   }
      //   return null
      // }
    }

    // zoom usado por evento Wheel
    ZoomStagePoiter(e, scaleBy) {
      var oldScale = this.stage.scaleX();
      var pointer = this.stage.getPointerPosition();

      var mousePointTo = {
        x: (pointer.x - this.stage.x()) / oldScale,
        y: (pointer.y - this.stage.y()) / oldScale,
      };

      // how to scale? Zoom in? Or zoom out?
      let direction = e.evt.deltaY > 0 ? 1 : -1;

      // when we zoom on trackpad, e.evt.ctrlKey is true
      // in that case lets revert direction
      if (e.evt.ctrlKey) {
        direction = -direction;
      }

      var newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

      this.stage.scale({ x: newScale, y: newScale });

      var newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };
      this.stage.position(newPos);
    }

    Screenshot() {
      return this.stage.toDataURL({
        // mimeType: 'image/jpeg',
        quality: 1,
        pixelRatio: 5,
        // width: 1280,
        // height: 720
      })
    }

    FitToScreen(padding = 0.10) {
      return new Promise((resolve, reject) => {
        try {
          // 1. Restablecer transformaciones para medir límites reales del contenido
          this.globalContainer.scale.set(1);
          this.picsContainer.scale.set(1);
          this.backsContainer.scale.set(1);
          this.bordersContainer.scale.set(1);
          this.labelsContainer.scale.set(1);

          // 2. Obtener los límites actuales del contenido dentro del contenador global
          const contentBounds = this.globalContainer.getLocalBounds();
          const contentWidth  = contentBounds.width;
          const contentHeight = contentBounds.height;

          // Validar que hay contenido
          if (contentWidth <= 0 || contentHeight <= 0) {
            console.warn('FitToScreen: No hay contenido para ajustar');
            resolve();
            return;
          }

          // 3. Obtener dimensiones del viewport (renderer)
          const viewportWidth = this.app.renderer.width;
          const viewportHeight = this.app.renderer.height;

          // 4. Calcular la escala óptima para encajar todo el contenido con padding
          // El padding se aplica como porcentaje del contenido para dejar márgenes
          const paddedWidth  = contentWidth  * (1 + padding);
          const paddedHeight = contentHeight * (1 + padding);

          const scaleX = viewportWidth  / paddedWidth;
          const scaleY = viewportHeight / paddedHeight;
          const scale  = Math.min(scaleX, scaleY);  // Escala uniforme (mantener aspecto)

          // 5. Actualizar zoomLevel y aplicar la escala calculada
          this.zoomLevel = scale;
          this.globalContainer.scale.set(scale, scale);

          // 6. Calcular desplazamiento para centrar el contenido en el canvas
          // Consideramos el bounding box del contenido (incluyendo su posición x/y)
          const scaledWidth = contentWidth * scale;
          const scaledHeight = contentHeight * scale;

          // Centrar horizontalmente y verticalmente
          const offsetX = (viewportWidth - scaledWidth) / 2 - contentBounds.x * scale;
          const offsetY = (viewportHeight - scaledHeight) / 2 - contentBounds.y * scale;

          // 7. Posicionar el contenedor global usando el desplazamiento calculado
          this.globalContainer.position.set(offsetX, offsetY);

          // Emitir evento de cambio de viewport para sincronización con Google Maps
          this._emitViewportChanged();

          resolve();
        } catch (error) {
          console.error('Error en FitToScreen:', error);
          reject(error);
        }
      })
    }

    Selecting(value, workLayer=null) {
      this.selecting = value
      this.workLayer = workLayer
      // Al entrar en modo selección/registro desactivamos el pan explícito para que
      // el arrastre dibuje la caja de selección y no mueva la vista (_canPan()).
      if (value) {
        this.dragging = false
        this.dragData = null
      }
    }

    Unselect() {
      this.selecting = false
      if (this.workLayer !== 'nodes') {
        this.picsContainer.children.forEach( (el) => {
            if( el.picSelected )
              el.SelectPic(el)
        } );
      } else {
        this.picsContainer.children.filter( (el) => {
          return el.picType === this.workLayer
        } ).forEach(p => {
          if (p.picSelected)
            p.SelectPic(p)
        });
      }
    }

    Dragging(value) {
      this.dragging = value
      this.dragData = null
    }

    /**
     * Obtiene las estadísticas de culling actuales
     * @returns {{ visible: number, total: number, culled: number }} Estadísticas de elementos renderizados
     */
    getCullingStats() {
      return this.cullingStats;
    }

    /**
     * Activa/desactiva el fondo transparente para mostrar Google Maps debajo
     * @param {boolean} transparent - Si true, hace el fondo transparente
     */
    setTransparentBackground(transparent) {
      if (!this.app) return;

      if (transparent) {
        // Hacer visible la transparencia del canvas (ya está inicializado con alpha 0)
        // Solo necesitamos quitar el fondo CSS y ocultar los backgrounds del pictograma
        if (this.app.canvas) {
          this.app.canvas.style.backgroundColor = 'transparent';
        }

        if (this.stage) {
          this.stage.style.backgroundColor = 'transparent';
        }

        // Ocultar el contenedor de fondos (labels, backgrounds) para ver mejor el mapa
        if (this.backsContainer) {
          this.backsContainer.visible = false;
        }

        this._transparentBackground = true;
      } else {
        // Restaurar el fondo visual con CSS (el canvas sigue siendo transparente internamente)
        if (this.app.canvas) {
          this.app.canvas.style.backgroundColor = '#ffffff';
        }

        if (this.stage) {
          this.stage.style.backgroundColor = '#ffffff';
        }

        // Mostrar el contenedor de fondos nuevamente
        if (this.backsContainer) {
          this.backsContainer.visible = true;
        }

        this._transparentBackground = false;
      }
    }

    /**
     * Verifica si el fondo transparente está activo
     * @returns {boolean}
     */
    isTransparentBackground() {
      return this._transparentBackground || false;
    }

    /**
     * Indica si con el zoom actual la línea de los elementos mediría menos del umbral
     * en pantalla. En ese caso se dibuja a 1px fijo (pixelLine) para que siga siendo
     * visible de lejos, igual que en AutoCAD.
     */
    _shouldUsePixelLines() {
      if (!this.globalContainer || !this.picsContainer) return false;
      const scale = Math.abs(this.globalContainer.scale.x * this.picsContainer.scale.x);
      return BORDER_WORLD_WIDTH * scale < PIXEL_LINE_SCREEN_THRESHOLD;
    }

    /**
     * Tras un zoom/pan/fit, redibuja los bordes solo si se cruzó el umbral de modo de línea.
     * Con debounce para no redibujar en medio de un zoom con la rueda.
     */
    _scheduleLinesModeCheck() {
      if (!this._useSprites) return;

      if (this._linesModeTimeout) clearTimeout(this._linesModeTimeout);

      this._linesModeTimeout = setTimeout(() => {
        this._linesModeTimeout = null;
        if (this._destroyed || !this.bordersContainer || !this.picsContainer) return;

        if (this._shouldUsePixelLines() !== this._bordersPixelLine) {
          this._createBordersForEntities(Array.from(this.picsContainer.children));
        }
      }, LINES_MODE_DEBOUNCE_MS);
    }

    /**
     * Emite un evento cuando el viewport cambia (zoom, pan, fit)
     * Usado para sincronizar con Google Maps
     * Throttled via requestAnimationFrame para evitar disparar decenas de eventos/seg durante drag
     */
    _emitViewportChanged() {
      // Comprobar (con debounce) si los bordes deben cambiar entre 1px fijo y ancho real
      this._scheduleLinesModeCheck();

      if (this._viewportChangeScheduled) return;
      this._viewportChangeScheduled = true;

      requestAnimationFrame(() => {
        this._viewportChangeScheduled = false;
        const bounds = this.getViewportBounds();
        if (bounds) {
          document.dispatchEvent(new CustomEvent('__epc_viewport_changed', {
            detail: bounds
          }));
        }
      });
    }

    /**
     * Obtiene los límites del viewport actual en coordenadas del pictograma
     * @returns {{ minX: number, maxX: number, minY: number, maxY: number }} Límites del viewport
     */
    getViewportBounds() {
      if (!this.app || !this.picsContainer) {
        return null;
      }

      const renderer = this.app.renderer;
      const topLeft = this.picsContainer.toLocal(new Point(0, 0));
      const bottomRight = this.picsContainer.toLocal(
        new Point(renderer.width, renderer.height)
      );

      return {
        minX: Math.min(topLeft.x, bottomRight.x),
        maxX: Math.max(topLeft.x, bottomRight.x),
        minY: Math.min(topLeft.y, bottomRight.y),
        maxY: Math.max(topLeft.y, bottomRight.y)
      };
    }

    MoveStage(stage,  location,  scale) {
      const { x, y } = location
      // const tween = new Konva.Tween({
      //   duration: 0.35,
      //   easing: Konva.Easings.EaseInOut,
      //   node: stage,
      //   onFinish: () => {
      //     fnCallback()
      //     tween.destroy
      //   },
      //   scaleX: (scale && scale.x) || 1,
      //   scaleY: (scale && scale.y) || 1,
      //   x,
      //   y,
      // })

      // tween.play()
      stage.x(x)
      stage.y(y)
      stage.scaleX((scale && scale.x) || 1)
      stage.scaleY((scale && scale.y) || 1)
      stage.batchDraw()
    }

    // Metodo que permite obtener la posicion que debe mostrarse un elemento respetando los limites del
    // contenedor
    GetPosition(discanceXMin, distanceYMin) {
      const mousePos = this.stage.getPointerPosition();

      let x = mousePos.x //- 190;
      let y = mousePos.y //- 40;

      var contenedorWidth = this.stage.width();
      var contenedorHeight = this.stage.height();

      if ( mousePos.x <= discanceXMin ) {
        // console.log('El punto está cerca del límite izquierdo del contenedor.');
      } else {
        // console.log('El punto no está cerca del límite izquierdo del contenedor.');
      }

      if (mousePos.x >= contenedorWidth - discanceXMin) {
        x = mousePos.x - discanceXMin
        // console.log('El punto está cerca del límite derecho del contenedor.');
      } else {
        // console.log('El punto no está cerca del límite derecho del contenedor.');
      }

      if (mousePos.y <= distanceYMin) {
        // console.log('El punto está cerca del límite superior del contenedor.');
      } else {
        // console.log('El punto no está cerca del límite superior del contenedor.');
      }

      if (mousePos.y >= contenedorHeight - distanceYMin) {
        y = mousePos.y - distanceYMin
        // console.log('El punto está cerca del límite inferior del contenedor.');
      } else {
        // console.log('El punto no está cerca del límite inferior del contenedor.');
      }

      return { x, y }
    }

    _OnResize() {
      if (!this.app) return;

      let containerWidth = this.domContainer.clientWidth;
      let containerHeight = this.domContainer.clientHeight;
      let stageWidth = containerWidth;
      let stageHeight = containerHeight;

      this.app.renderer.resize(stageWidth, stageHeight);

      // Recentrar el contenido manteniendo el zoom actual
      this.CenterContent();
    }

    /**
     * Centra el contenido en el viewport manteniendo el nivel de zoom actual
     * Útil para resize y pantalla completa donde se quiere preservar el zoom
     */
    CenterContent() {
      if (!this.app || !this.globalContainer) return;

      const contentBounds = this.globalContainer.getLocalBounds();
      const scale = this.globalContainer.scale.x; // Mantener zoom actual

      const scaledWidth = contentBounds.width * scale;
      const scaledHeight = contentBounds.height * scale;

      const offsetX = (this.app.renderer.width - scaledWidth) / 2 - contentBounds.x * scale;
      const offsetY = (this.app.renderer.height - scaledHeight) / 2 - contentBounds.y * scale;

      this.globalContainer.position.set(offsetX, offsetY);
    }

    Resize() {
      this.stage.width(this.domContainer.offsetWidth)
      this.stage.height(this.domContainer.offsetHeight)

      this.stage.scale({
        x: this.domContainer.offsetWidth / this.stage.width(),
        y: this.domContainer.offsetWidth / this.stage.height()
      });

      // Vuelve a dibujar la capa de Konva
      // this.$refs.refLayerText.getNode().draw();
      // this.$refs.layer.getNode().draw();
      this.stage.draw()
      // this.FitViewKonva()
      this.FitToScreen()
    }

    _GetOffset() {
      if (this.bounds === undefined)
        return { x: 0, y: 0 } // si no existe bounds no hay desplazamiento

      return {
        x: Math.abs( this.bounds.minX  - this.origin.x ), // desplazameinto en x
        y: Math.abs( this.bounds.maxY  - this.origin.y ) // desplazamaiento en y
      }
    }

    _OnPointerEvent(e) {
        const canvasRect = e.target.getBoundingClientRect()
        const canvasCoord = {x: e.clientX - canvasRect.left, y: e.clientY - canvasRect.top}
        this._Emit(e.type, {
            domEvent: e,
            canvasCoord,
            position: this._CanvasToSceneCoord(canvasCoord.x, canvasCoord.y)
        })
    }

    // _OnResize(entry) {
    //     this.SetSize(Math.floor(entry.contentRect.width), Math.floor(entry.contentRect.height))
    // }

    _LoadBatch(scene, batch) {

        const objects = new Batch(this, scene, batch).CreateObjects()

        const layer = this.layers.get(batch.key.layerName)

        for (const obj of objects) {
            this.scene.add(obj)
            if (layer) {
                layer.PushObject(obj)
            }
        }
    }

    _GetColorLayer(nameLayer) {
      let layer = this.layers.find((l) => l.name === nameLayer)
      return this._GetCssColor(layer.color)
    }

    _GetCssColor(value) {
        let s = value.toString(16)
        while (s.length < 6) {
            s = "0" + s
        }
        return "#" + s
    }

    /** Ensure the color is contrast enough with current background color.
     * @param color {number} RGB value.
     * @return {number} RGB value to use for rendering.
     */
    _TransformColor(color) {
        if (!this.options.colorCorrection && !this.options.blackWhiteInversion) {
            return color
        }
        /* Just black and white inversion. */
        const bkgLum = Luminance(this.clearColor)
        if (color === 0xffffff && bkgLum >= 0.8) {
            return 0
        }
        if (color === 0 && bkgLum <= 0.2) {
            return 0xffffff
        }
        if (!this.options.colorCorrection) {
            return color
        }
        const fgLum = Luminance(color)
        const MIN_TARGET_RATIO = 1.5
        const contrast = ContrastRatio(color, this.clearColor)
        const diff = contrast >= 1 ? contrast : 1 / contrast
        if (diff < MIN_TARGET_RATIO) {
            let targetLum
            if (bkgLum > 0.5) {
                targetLum = bkgLum / 2
            } else {
                targetLum = bkgLum * 2
            }
            if (targetLum > fgLum) {
                color = Lighten(color, targetLum / fgLum)
            } else {
                color = Darken(color, fgLum / targetLum)
            }
        }
        return color
    }

    _Emit(eventName, data = null) {
      this.canvas.dispatchEvent(new CustomEvent(EVENT_NAME_PREFIX + eventName, { detail: data }))
    }
}

PixiViewer.MessageLevel = MessageLevel

PixiViewer.DefaultOptions = {
    canvasWidth: 400,
    canvasHeight: 300,
    /** Automatically resize canvas when the container is resized. This options utilizes
     *  ResizeObserver API which is still not fully standardized. The specified canvas size is
     *  ignored if the option is enabled.
     */
    autoResize: false,
    /** Frame buffer clear color alpha value. */
    clearAlpha: 1.0,
    /** Use alpha channel in a framebuffer. */
    canvasAlpha: false,
    /** Assume premultiplied alpha in a framebuffer. */
    canvasPremultipliedAlpha: true,
    /** Use antialiasing. May degrade performance on poor hardware. */
    antialias: true,
    /** Correct entities colors to ensure that they are always visible with the current background
     * color.
     */
    colorCorrection: false,
    /** Simpler version of colorCorrection - just invert pure white or black entities if they are
     * invisible on current background color.
     */
    blackWhiteInversion: true,
    /** Size in pixels for rasterized points (dot mark). */
    pointSize: 2,
    /** Retain the simple object representing the parsed DXF - will consume a lot of additional
     * memory.
     */
    retainParsedDxf: false,
    /** Whether to preserve the buffers until manually cleared or overwritten. */
    preserveDrawingBuffer: false,
    /** Encoding to use for decoding DXF file text content. DXF files newer than DXF R2004 (AC1018)
     * use UTF-8 encoding. Older files use some code page which is specified in $DWGCODEPAGE header
     * variable. Currently parser is implemented in such a way that encoding must be specified
     * before the content is parsed so there is no chance to use this variable dynamically. This may
     * be a subject for future changes. The specified value should be suitable for passing as
     * `TextDecoder` constructor `label` parameter.
     */
    fileEncoding: "utf-8"
}

class Layer {
    constructor(name, displayName, color) {
        this.name = name
        this.displayName = displayName
        this.color = color
        this.objects = []
    }

    PushObject(obj) {
        this.objects.push(obj)
    }

    Dispose() {
        for (const obj of this.objects) {
            obj.geometry.dispose()
        }
        this.objects = null
    }
}

// De aquí para abajo es para tratamiento del codigo de color

/** Transform sRGB color component to linear color space. */
function LinearColor(c) {
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** Transform linear color component to sRGB color space. */
function SRgbColor(c) {
    return c < 0.003 ? c * 12.92 : Math.pow(c, 1 / 2.4) * 1.055 - 0.055
}

/** Get relative luminance value for a color.
 * https://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
 * @param color {number} RGB color value.
 * @return {number} Luminance value in range [0; 1].
 */
function Luminance(color) {
    const r = LinearColor(((color & 0xff0000) >>> 16) / 255)
    const g = LinearColor(((color & 0xff00) >>> 8) / 255)
    const b = LinearColor((color & 0xff) / 255)

    return r * 0.2126 + g * 0.7152 + b * 0.0722
}

/**
 * Get contrast ratio for a color pair.
 * https://www.w3.org/TR/2008/REC-WCAG20-20081211/#contrast-ratiodef
 * @param c1
 * @param c2
 * @return {number} Contrast ratio between the colors. Greater than one if the first color color is
 *  brighter than the second one.
 */
function ContrastRatio(c1, c2) {
    return (Luminance(c1) + 0.05) / (Luminance(c2) + 0.05)
}

function HlsToRgb({h, l, s}) {
    let r, g, b
    if (s === 0) {
        /* Achromatic */
        r = g = b = l
    } else {
        function hue2rgb(p, q, t) {
            if (t < 0) {
                t += 1
            }
            if (t > 1) {
                t -= 1
            }
            if (t < 1/6) {
                return p + (q - p) * 6 * t
            }
            if (t < 1/2) {
                return q
            }
            if (t < 2/3) {
                return p + (q - p) * (2/3 - t) * 6
            }
            return p
        }

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s
        const p = 2 * l - q
        r = hue2rgb(p, q, h + 1/3)
        g = hue2rgb(p, q, h)
        b = hue2rgb(p, q, h - 1/3)
    }

    return (Math.min(Math.floor(SRgbColor(r) * 256), 255) << 16) |
           (Math.min(Math.floor(SRgbColor(g) * 256), 255) << 8) |
            Math.min(Math.floor(SRgbColor(b) * 256), 255)
}

function RgbToHls(color) {
    const r = LinearColor(((color & 0xff0000) >>> 16) / 255)
    const g = LinearColor(((color & 0xff00) >>> 8) / 255)
    const b = LinearColor((color & 0xff) / 255)

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h, s
    const l = (max + min) / 2

    if (max === min) {
        /* Achromatic */
        h = s = 0
    } else {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        switch (max) {
        case r:
            h = (g - b) / d + (g < b ? 6 : 0)
            break;
        case g:
            h = (b - r) / d + 2
            break
        case b:
            h = (r - g) / d + 4
            break
        }
        h /= 6
    }

    return {h, l, s}
}

function Lighten(color, factor) {
    const hls = RgbToHls(color)
    hls.l *= factor
    if (hls.l > 1) {
        hls.l = 1
    }
    return HlsToRgb(hls)
}

function Darken(color, factor) {
    const hls = RgbToHls(color)
    hls.l /= factor
    return HlsToRgb(hls)
}
